import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authAPI, projectAPI, threadAPI, getStats } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { FolderOpen, MessageSquare, Users, TrendingUp, Plus, ArrowRight, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [myProjects, setMyProjects] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get user from location state or fetch
        let currentUser = location.state?.user;
        if (!currentUser) {
          const userResponse = await authAPI.getMe();
          currentUser = userResponse.data;
        }
        setUser(currentUser);

        // Fetch stats and user's content
        const [statsRes, projectsRes, threadsRes] = await Promise.all([
          getStats(),
          projectAPI.getAll({ limit: 5 }),
          threadAPI.getAll({ limit: 5 }),
        ]);

        setStats(statsRes.data);
        
        // Filter to show user's own content
        setMyProjects(projectsRes.data.filter(p => p.author_id === currentUser.user_id).slice(0, 3));
        setMyPosts(threadsRes.data.filter(p => p.author_id === currentUser.user_id).slice(0, 3));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [location.state]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <Avatar className="w-16 h-16">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="text-xl bg-indigo-100 text-indigo-700">
                {user?.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Welcome back, {user?.name?.split(' ')[0]}!
              </h1>
              <p className="text-slate-600">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <Link to="/projects/create">
            <Card className="card-hover cursor-pointer border-indigo-200 bg-indigo-50/50" data-testid="quick-create-project">
              <CardContent className="flex items-center gap-4 p-6">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Create Project</h3>
                  <p className="text-sm text-slate-600">Share your hackathon work</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/forum/create">
            <Card className="card-hover cursor-pointer border-emerald-200 bg-emerald-50/50" data-testid="quick-create-post">
              <CardContent className="flex items-center gap-4 p-6">
                <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Start Discussion</h3>
                  <p className="text-sm text-slate-600">Post in the forum</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="flex items-center gap-4 p-6">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.projects}</p>
                  <p className="text-sm text-slate-600">Projects</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex items-center gap-4 p-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.posts}</p>
                  <p className="text-sm text-slate-600">Forum Posts</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex items-center gap-4 p-6">
                <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-lime-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.users}</p>
                  <p className="text-sm text-slate-600">Members</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex items-center gap-4 p-6">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.comments}</p>
                  <p className="text-sm text-slate-600">Comments</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* My Content */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* My Projects */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">My Projects</CardTitle>
              <Link to="/projects">
                <Button variant="ghost" size="sm" className="text-indigo-600">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {myProjects.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 mb-4">No projects yet</p>
                  <Link to="/projects/create">
                    <Button size="sm" className="bg-indigo-600">
                      Create Your First Project
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {myProjects.map((project) => (
                    <Link
                      key={project.project_id}
                      to={`/projects/${project.project_id}`}
                      className="block p-3 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`my-project-${project.project_id}`}
                    >
                      <h4 className="font-medium text-slate-900 truncate">{project.title}</h4>
                      <p className="text-sm text-slate-500">
                        {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Posts */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">My Forum Posts</CardTitle>
              <Link to="/forum">
                <Button variant="ghost" size="sm" className="text-emerald-600">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {myPosts.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 mb-4">No posts yet</p>
                  <Link to="/forum/create">
                    <Button size="sm" className="bg-emerald-600">
                      Start a Discussion
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {myPosts.map((post) => (
                    <Link
                      key={post.post_id}
                      to={`/forum/${post.post_id}`}
                      className="block p-3 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`my-post-${post.post_id}`}
                    >
                      <h4 className="font-medium text-slate-900 truncate">{post.title}</h4>
                      <p className="text-sm text-slate-500">
                        {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
